import { useCallback, useState } from "react";
import * as portfolioService from "../services/portfolioService";
import type {
  PortfolioSummary,
  UpsertPortfolioPayload,
  AccountBalances,
} from "../types/api";

interface UsePortfolioState {
  summary: PortfolioSummary | null;
  balances: AccountBalances | null;
  loading: boolean;
  balancesLoading: boolean;
  error: string | null;
  fetchSummary: (userId: number) => Promise<void>;
  fetchBalances: (userId: number) => Promise<void>;
  upsertPosition: (payload: UpsertPortfolioPayload) => Promise<void>;
}

/**
 * Hook maison pour centraliser l'état du portefeuille.
 */
export const usePortfolio = (): UsePortfolioState => {
  const [summary, setSummary] = useState<PortfolioSummary | null>(null);
  const [balances, setBalances] = useState<AccountBalances | null>(null);
  const [loading, setLoading] = useState(false);
  const [balancesLoading, setBalancesLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchSummary = useCallback(async (userId: number) => {
    setLoading(true);
    setError(null);
    try {
      const data = await portfolioService.getPortfolioSummary(userId);
      setSummary(data);
    } catch (err) {
      setError((err as string) ?? "Impossible de charger le portefeuille");
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchBalances = useCallback(async (userId: number) => {
    setBalancesLoading(true);
    try {
      const data = await portfolioService.getAccountBalances(userId);
      setBalances(data);
    } catch (err) {
      setError((err as string) ?? "Impossible de charger le solde");
    } finally {
      setBalancesLoading(false);
    }
  }, []);

  const upsertPosition = useCallback(
    async (payload: UpsertPortfolioPayload) => {
      await portfolioService.upsertPortfolioPosition(payload);
      await fetchSummary(payload.userId);
      await fetchBalances(payload.userId);
    },
    [fetchSummary, fetchBalances]
  );

  return {
    summary,
    balances,
    loading,
    balancesLoading,
    error,
    fetchSummary,
    fetchBalances,
    upsertPosition,
  };
};
